import {
	Card,
	Tabs,
	Table,
	Button,
	Form,
	DatePicker,
	InputNumber,
	Input,
	Upload,
	Row,
	Col,
	Statistic,
	Progress,
	Empty,
	Tag,
	Modal,
} from "antd";
import {
	PlusOutlined,
	InboxOutlined,
	ClockCircleOutlined,
	CheckCircleOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import ReactApexChart from "react-apexcharts";
import PageTitle from "../page-header/PageHeader";
import {
	DUMMY_EMPLOYEE_BALANCE,
	DUMMY_EMPLOYEE_REQUESTS,
} from "./constants/defaultPolicy";
import "./compOffPolicy.css";

const { TabPane } = Tabs;
const { Dragger } = Upload;

export default function CompOffEmployeeSelfService() {
	const [activeTab, setActiveTab] = useState("available");
	const [requestModalOpen, setRequestModalOpen] = useState(false);
	const [form] = Form.useForm();
	const [requests, setRequests] = useState(DUMMY_EMPLOYEE_REQUESTS);

	const balance = DUMMY_EMPLOYEE_BALANCE;

	const usageChartOptions = {
		chart: { type: "donut", fontFamily: "inherit" },
		labels: ["Available", "Used", "Pending", "Expiring"],
		colors: ["#16a34a", "#94a3b8", "#f59e0b", "#ef4444"],
		legend: { position: "bottom" },
	};
	const usageChartSeries = [
		balance.available,
		balance.used,
		balance.pending,
		balance.expiringSoon,
	];

	const handleSubmitRequest = (values) => {
		const newReq = {
			id: Date.now(),
			workedDate: values.workedDate?.format("YYYY-MM-DD"),
			hours: values.workedHours,
			reason: values.reason,
			status: "Pending",
		};
		setRequests((prev) => ({
			...prev,
			pending: [...prev.pending, newReq],
		}));
		setRequestModalOpen(false);
		form.resetFields();
	};

	const availableColumns = [
		{ title: "Worked Date", dataIndex: "workedDate", key: "workedDate" },
		{ title: "Credit", dataIndex: "credit", key: "credit", render: (v) => `${v} day(s)` },
		{ title: "Expiry", dataIndex: "expiryDate", key: "expiryDate" },
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (s) => <Tag color="green">{s}</Tag>,
		},
	];

	const pendingColumns = [
		...availableColumns.slice(0, 2),
		{ title: "Hours", dataIndex: "hours", key: "hours" },
		{ title: "Reason", dataIndex: "reason", key: "reason", ellipsis: true },
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (s) => <Tag color="orange">{s}</Tag>,
		},
	];

	const historyColumns = [
		{ title: "Worked Date", dataIndex: "workedDate", key: "workedDate" },
		{ title: "Credit", dataIndex: "credit", key: "credit" },
		{ title: "Used On", dataIndex: "usedDate", key: "usedDate" },
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (s) => <Tag>{s}</Tag>,
		},
	];

	const expiringColumns = [
		{ title: "Worked Date", dataIndex: "workedDate", key: "workedDate" },
		{ title: "Credit", dataIndex: "credit", key: "credit" },
		{ title: "Expiry", dataIndex: "expiryDate", key: "expiryDate" },
		{
			title: "Days Left",
			dataIndex: "daysLeft",
			key: "daysLeft",
			render: (d) => (
				<Tag color={d <= 7 ? "red" : "orange"} icon={<ClockCircleOutlined />}>
					{d} days
				</Tag>
			),
		},
	];

	return (
		<div className="compoff-employee-page">
			<PageTitle
				title="My Comp-Off"
				subtitle="View balance, submit requests, and track history"
			/>

			<Row gutter={[16, 16]} className="mb-4">
				<Col xs={12} sm={6}>
					<Card className="compoff-balance-card">
						<Statistic
							title="Available"
							value={balance.available}
							suffix="days"
							valueStyle={{ color: "#16a34a" }}
						/>
					</Card>
				</Col>
				<Col xs={12} sm={6}>
					<Card className="compoff-balance-card">
						<Statistic title="Pending" value={balance.pending} suffix="days" />
					</Card>
				</Col>
				<Col xs={12} sm={6}>
					<Card className="compoff-balance-card">
						<Statistic title="Used" value={balance.used} suffix="days" />
					</Card>
				</Col>
				<Col xs={12} sm={6}>
					<Card className="compoff-balance-card">
						<Statistic
							title="Expiring Soon"
							value={balance.expiringSoon}
							suffix="days"
							valueStyle={{ color: "#f59e0b" }}
						/>
					</Card>
				</Col>
			</Row>

			<Row gutter={[16, 16]} className="mb-4">
				<Col xs={24} md={14}>
					<Card title="Usage Overview" className="compoff-chart-card">
						<ReactApexChart
							options={usageChartOptions}
							series={usageChartSeries}
							type="donut"
							height={280}
						/>
					</Card>
				</Col>
				<Col xs={24} md={10}>
					<Card title="Utilization" className="compoff-chart-card">
						<p className="text-muted mb-2">Annual comp-off utilization</p>
						<Progress
							percent={Math.round(
								(balance.used / (balance.available + balance.used)) * 100
							)}
							strokeColor="#16a34a"
						/>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							className="compoff-request-btn mt-4"
							block
							onClick={() => setRequestModalOpen(true)}
						>
							Request Comp-Off
						</Button>
					</Card>
				</Col>
			</Row>

			<Card className="compoff-employee-tabs-card">
				<Tabs activeKey={activeTab} onChange={setActiveTab}>
					<TabPane tab="Available Comp-Offs" key="available">
						<Table
							dataSource={requests.available}
							columns={availableColumns}
							rowKey="id"
							pagination={false}
							locale={{ emptyText: <Empty description="No available comp-offs" /> }}
						/>
					</TabPane>
					<TabPane tab="Pending Requests" key="pending">
						<Table
							dataSource={requests.pending}
							columns={pendingColumns}
							rowKey="id"
							pagination={false}
						/>
					</TabPane>
					<TabPane tab="Request History" key="history">
						<Table
							dataSource={requests.history}
							columns={historyColumns}
							rowKey="id"
							pagination={{ pageSize: 5 }}
						/>
					</TabPane>
					<TabPane tab="Expiring Soon" key="expiring">
						<Table
							dataSource={requests.expiring}
							columns={expiringColumns}
							rowKey="id"
							pagination={false}
						/>
					</TabPane>
				</Tabs>
			</Card>

			<Modal
				title="Request Comp-Off"
				open={requestModalOpen}
				onCancel={() => setRequestModalOpen(false)}
				footer={null}
				width={560}
				destroyOnClose
			>
				<Form form={form} layout="vertical" onFinish={handleSubmitRequest}>
					<Form.Item
						name="workedDate"
						label="Worked Date"
						rules={[{ required: true, message: "Required" }]}
					>
						<DatePicker className="w-full" />
					</Form.Item>
					<Form.Item
						name="workedHours"
						label="Worked Hours"
						rules={[{ required: true, message: "Required" }]}
					>
						<InputNumber min={1} max={24} step={0.5} className="w-full" />
					</Form.Item>
					<Form.Item
						name="reason"
						label="Reason"
						rules={[{ required: true, message: "Required" }]}
					>
						<Input.TextArea rows={3} placeholder="Describe work performed" />
					</Form.Item>
					<Form.Item name="attendanceRef" label="Attendance Reference">
						<Input placeholder="Attendance ID or date reference" />
					</Form.Item>
					<Form.Item name="attachment" label="Attachment">
						<Dragger beforeUpload={() => false} maxCount={1}>
							<p className="ant-upload-drag-icon">
								<InboxOutlined />
							</p>
							<p className="ant-upload-text">Upload supporting document</p>
						</Dragger>
					</Form.Item>
					<Button
						type="primary"
						htmlType="submit"
						icon={<CheckCircleOutlined />}
						block
						className="compoff-request-btn"
					>
						Submit Request
					</Button>
				</Form>
			</Modal>
		</div>
	);
}
